import { useState, useEffect, useCallback, useMemo } from "react";
import CustomerService from "../../../rest/customerService";
import { useRealTimeList } from "../../../ws/useRealtimeList";

export function useCustomerData(clientType, filters, page, pageSize, sort, user) {
    const [pagedData, setPagedData] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);

        const clientCode = user?.client_code || user?.clientCode;
        if (!clientCode) return;

        setLoading(true);
        try {
            const apiFilters = {};

            if (filters.status?.length === 1) {
                const val = filters.status[0]?.value ?? filters.status[0];
                if (val !== null && val !== undefined) apiFilters.userStatus = val;
            }

            if (clientType === 'students' && filters.grade?.length > 0) {
                apiFilters.grades = filters.grade.map(g => g?.value ?? g);
            }

            if (clientType !== 'parents' && filters.creditStatus?.length === 1) {
                const val = filters.creditStatus[0]?.value ?? filters.creditStatus[0];
                if (val === "negative") {
                    apiFilters.negativeBalance = true;
                } else if (val === "positive") {
                    apiFilters.negativeBalance = false;
                    apiFilters.minCredit = 0.01;
                } else if (val === "zero") {
                    apiFilters.minCredit = 0;
                    apiFilters.maxCredit = 0;
                }
            }

            const queryParams = {
                search: filters.search || '',
                ...apiFilters,
                page,
                size: pageSize,
            };

            if (sort) queryParams.sort = sort;

            let response;
            if (clientType === 'students') {
                response = await CustomerService.getAllStudents(queryParams);
            } else if (clientType === 'parents') {
                response = await CustomerService.getAllParents(queryParams);
            } else if (clientType === 'employees') {
                response = await CustomerService.getAllEmployees(queryParams);
            }

            if (response) {
                setPagedData(response.content || []);
                setTotalPages(response.totalPages || 0);
                setTotalElements(response.totalElements || 0);
            }
        } catch (error) {
            console.error(`Erro ao buscar dados de ${clientType}:`, error);
        } finally {
            setLoading(false);
        }
    }, [clientType, filters, page, pageSize, sort, user]);

    const gradeFilterKey = clientType === 'students' ? JSON.stringify(filters.grade || []) : '';
    const activeGrades = useMemo(() => {
        if (clientType !== 'students' || !filters.grade?.length) return null;
        return filters.grade.map(g => g?.value ?? g);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientType, gradeFilterKey]);

    const matchesFilters = useCallback((item) => {
        if (activeGrades && !activeGrades.includes(item.grade)) return false;
        return true;
    }, [activeGrades]);

    const handleRealtimeMessage = useCallback(() => {
        fetchData(true);
    }, [fetchData]);

    useRealTimeList({
        clientCode: user?.client_code,
        clientType: clientType,
        setPagedData,
        setTotalElements,
        onMessageReceived: handleRealtimeMessage,
        matchesFilters
    });
    useEffect(() => {
        const timeout = setTimeout(fetchData, 300);
        return () => clearTimeout(timeout);
    }, [fetchData]);

    return {
        pagedData,
        totalElements,
        totalPages,
        loading,
        reloadData: fetchData
    };
}